// i18n — the console's translation layer (replaces Transloco).
//
// Dictionaries are the same JSON files Transloco used (public/i18n/<lang>.json,
// nested keys, `{{param}}` interpolation). They are fetched with a RELATIVE url
// so the injected <base href> (path-routed deployments, /<slug>/) applies.
//
// `t(key, params)` is reactive: read inside JSX or a memo, it re-renders when
// the active language or its dictionary changes. Missing keys fall back to
// en-CA, then to the key itself.
import { untrack } from 'solid-js';
import { signal } from './signal';

export type Lang = 'en-CA' | 'fr-CA';
export const FALLBACK_LANG: Lang = 'en-CA';

type Dict = Record<string, unknown>;
export type TranslateParams = Record<string, unknown>;

const dicts = signal<Partial<Record<Lang, Dict>>>({});
const active = signal<Lang>(FALLBACK_LANG);
const inflight = new Map<Lang, Promise<void>>();

function lookup(dict: Dict | undefined, key: string): string | undefined {
  if (!dict) return undefined;
  let cur: unknown = dict;
  for (const part of key.split('.')) {
    if (cur && typeof cur === 'object' && part in (cur as Dict)) cur = (cur as Dict)[part];
    else return undefined;
  }
  return typeof cur === 'string' ? cur : undefined;
}

function interpolate(text: string, params?: TranslateParams): string {
  if (!params) return text;
  return text.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (m, name: string) => {
    const v = params[name];
    return v === undefined || v === null ? m : String(v);
  });
}

/** Translate `key` in the active language (reactive). */
export function t(key: string, params?: TranslateParams): string {
  const all = dicts();
  const text = lookup(all[active()], key) ?? lookup(all[FALLBACK_LANG], key);
  if (text === undefined) {
    if (import.meta.env.DEV && Object.keys(all).length) console.warn(`[i18n] missing key: ${key}`);
    return key;
  }
  return interpolate(text, params);
}

async function load(lang: Lang): Promise<void> {
  // Untracked: also called from effect apply phases (stateService language sync).
  if (untrack(() => dicts()[lang])) return;
  let p = inflight.get(lang);
  if (!p) {
    p = (async () => {
      const url = new URL(`i18n/${lang}.json`, document.baseURI).href;
      const res = await fetch(url, { cache: 'no-cache' });
      if (!res.ok) throw new Error(`i18n ${lang}: HTTP ${res.status}`);
      const dict = (await res.json()) as Dict;
      dicts.update((d) => ({ ...d, [lang]: dict }));
    })().finally(() => inflight.delete(lang));
    inflight.set(lang, p);
  }
  await p;
}

/** Activate a language once its dictionary (and the fallback) is loaded. */
export async function useLang(lang: Lang): Promise<void> {
  await Promise.all([load(FALLBACK_LANG), load(lang)]).catch((e) => console.error(e));
  active.set(lang);
}

/** The Transloco-shaped object some helpers take (`translate(key, params)`). */
export const i18n = {
  translate: t,
  activeLang: active.asReadonly(),
};
export type Translator = typeof i18n;
